import React, { useState, useEffect } from "react";
import OtpScreen from "common/OTPScreen"; // Assuming this accepts isOpen, onSubmit, numDigits
import { useStyles } from "./totp.styles";
import { Button, Box } from "@material-ui/core";
import ScannerScreen from "./scannerScreen"; // Assuming this accepts a scanner prop
import MessageTOTP from "./messageTOTP"; // Assuming this accepts title, subTitle, type
import { Step } from "./index"; // Assuming this is where Step interface is defined
import AppConstants from "../../constants";
import { TextConstants } from "constants/TextConstants";
const TOTP_STEPS = AppConstants.USERS_CONSTANTS.TOTP_STEPS;

interface OverlayProps {
  steps: Step[];
  scanner?: string; // Replace with proper type if known (e.g., string URL or Blob)
  onClose?: () => void; // Added for closing the overlay
  isTotpSwitchEnabled?: boolean; // Added for switch state
  onStepChange?: (stepId: string) => void; // Callback for step change
}

const TotpSteps: React.FC<OverlayProps> = ({ steps, scanner, onClose, isTotpSwitchEnabled, onStepChange }) => {
  const classes = useStyles();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [otp, setOtp] = useState<string[]>(Array(6).fill("")); // OTP as array of digits

  // Fetch QR code when scanner step is active
  useEffect(() => {
    const currentStep = steps[currentStepIndex];
    if (onStepChange) {
      onStepChange(currentStep.id); // Notify parent of current step
    }
  }, [currentStepIndex, steps, onStepChange]);

  // Handle moving to the next step
  const handleNext = async (
    stepLogic?: Step["onContinue"],
    otpValue?: string
  ) => {
    if (stepLogic) {
      const otpToUse = otpValue || otp.join("");
      const result = await stepLogic({ otp: otpToUse });
      if (result?.nextStep) {
        const nextStepIndex = steps.findIndex((s) => s.id === result.nextStep);
        if (nextStepIndex !== -1) {
          setCurrentStepIndex(nextStepIndex);
          setOtp(Array(6).fill(""));
        } else {
          console.error("Invalid nextStep:", result.nextStep);
        }
      }
    } else if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
      setOtp(Array(6).fill(""));
    } else if (onClose) {
      onClose();
    }
  };

  const handleResendCode = async (stepLogic?: Step["onContinue"]) => {
    try {
      if (stepLogic) {
        await handleNext(stepLogic);
      }
    } catch (error) {
      console.error("Error resending OTP:", error);
    }
  };

  // Guard clause
  if (!steps || steps.length === 0) return null;

  const currentStep = steps[currentStepIndex];

  return (
    <Box className="overlay">
      <Box className="overlay-content">
        {currentStep.type === TOTP_STEPS.TYPES.SCANNER && (
          <ScannerScreen
            scanner={scanner} // Pass QR code URL or fallback to scanner prop
          />
        )}
        {currentStep.type === TOTP_STEPS.TYPES.OTP && (
          <OtpScreen
            isOpen={true}
            onSubmit={(otpValue: string) => {
              setOtp(otpValue.split("")); // Update local OTP state
              handleNext(currentStep.onContinue, otpValue); // Trigger next step
            }}
            numDigits={6}
          />
        )}
        {currentStep.type === TOTP_STEPS.TYPES.MESSAGE && (
          <MessageTOTP
            title={currentStep.message || ""}
            id={
              currentStep.id === TOTP_STEPS.IDS.SUCCESS
                ? TOTP_STEPS.IDS.SUCCESS
                : TOTP_STEPS.IDS.ERROR
            } // Assuming type based on id
            onResendCode={
              currentStep.id === "error"
                ? () => handleResendCode(currentStep.onContinue)
                : undefined
            }
            isSwitchOn={isTotpSwitchEnabled}
          />
        )}

        {/* Buttons for non-OTP steps */}
        {currentStep.type !== TOTP_STEPS.TYPES.OTP && (
          <Box mt={3} display="flex" justifyContent="center">
            {currentStep.showContinue && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleNext(currentStep.onContinue)}
                className={classes.continueBtn}
              >
                {TextConstants.CONTINUE}
              </Button>
            )}
            {currentStep.showDone && (
              <Button
                variant="contained"
                color="primary"
                className={classes.continueBtn}
                onClick={currentStep.onDone}
              >
                {TextConstants.DONE}
              </Button>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TotpSteps;
